import { Component, Element, Host, Prop, State, h } from '@stencil/core';
import { __, _n, sprintf } from '@wordpress/i18n';
import apiFetch from '../../../../functions/fetch';
import { addQueryArgs } from '@wordpress/url';
import { Checkout, Fulfillment, LineItem, Order, Product } from 'src/types';

@Component({
  tag: 'sc-fulfillments',
  styleUrl: 'sc-fulfillments.scss',
  shadow: true,
})
export class ScFulfillments {
  @Element() el: HTMLScFulfillmentsElement;

  @Prop() orderId: string;
  @Prop() heading: string;

  /** Show the "Order Received" notice for shippable orders with no fulfillments yet. */
  @Prop() showPreShipmentNotice: boolean = true;

  /** Holds fulfillments. */
  @State() fulfillments: Fulfillment[];

  /** Order is shippable and paid/processing but nothing has been fulfilled yet. */
  @State() awaitingFulfillment: boolean = false;

  /** Loading state */
  @State() loading: boolean;
  @State() busy: boolean;

  /** Error message */
  @State() error: string;

  componentDidLoad() {
    this.fetch();
  }

  async fetch() {
    try {
      this.busy = true;
      const fulfillments = (await apiFetch({
        path: addQueryArgs(`surecart/v1/fulfillments`, {
          // Expand only the top-level shipment for tracking — never rates/selected_rate/from_contact, which would leak label costs and the warehouse address to customers.
          expand: ['trackings', 'shipments', 'fulfillment_items', 'fulfillment_item.line_item', 'line_item.price', 'price.product'],
          order_ids: [this.orderId],
          shipment_status: ['shipped', 'delivered', 'in_transit', 'label_purchased', 'unshipped', 'returned', 'failed'],
        }),
      })) as Fulfillment[];
      // Voiding a label resets shipment_status to unshipped; the voided shipment is the marker a label existed. Keep those visible, hide never-labeled unshipped fulfillments.
      this.fulfillments = fulfillments.filter(
        fulfillment => fulfillment?.shipment_status !== 'unshipped' || (fulfillment?.shipments?.data || []).some(shipment => shipment?.status === 'voided'),
      );
      // Only pay for the order fetch when there's nothing to show.
      if (!this.fulfillments.length && this.showPreShipmentNotice) {
        await this.checkAwaitingFulfillment();
      }
    } catch (e) {
      console.error(this.error);
      this.error = e?.message || __('Something went wrong', 'surecart');
    } finally {
      this.busy = false;
    }
  }

  /** A paid/processing order with a shippable item and no fulfillments gets an "Order Received" notice. Fails silent — worst case we hide the notice, not the page. */
  async checkAwaitingFulfillment() {
    try {
      const order = (await apiFetch({
        path: addQueryArgs(`surecart/v1/orders/${this.orderId}`, {
          expand: ['checkout', 'checkout.line_items', 'line_item.price', 'price.product'],
        }),
      })) as Order;
      const hasShippableItem = (((order?.checkout as Checkout)?.line_items?.data || []) as LineItem[]).some(item => !!(item?.price?.product as Product)?.shipping_enabled);
      this.awaitingFulfillment = hasShippableItem && ['paid', 'processing'].includes(order?.status);
    } catch (e) {
      console.error(e);
    }
  }

  renderLoading() {
    return (
      <sc-flex flexDirection="column" style={{ gap: '1em' }}>
        <sc-skeleton style={{ width: '20%', display: 'inline-block' }}></sc-skeleton>
        <sc-skeleton style={{ width: '60%', display: 'inline-block' }}></sc-skeleton>
        <sc-skeleton style={{ width: '40%', display: 'inline-block' }}></sc-skeleton>
      </sc-flex>
    );
  }

  renderOrderReceived() {
    return (
      <sc-spacing style={{ '--spacing': 'var(--sc-spacing-large)' }}>
        <sc-dashboard-module>
          <span slot="heading">{this.heading || __('Shipment', 'surecart')}</span>
          <sc-card noPadding>
            <sc-stacked-list>
              <sc-stacked-list-row>
                <div class="fulfillment__header">
                  {/* Deliberately not sc-fulfillment-shipping-status-badge — no shipment record exists yet, so shipment statuses (incl. "Preparing to Ship") don't apply. */}
                  <sc-tag type="info">{__('Order Received', 'surecart')}</sc-tag>
                  <div>{__('Your item will ship soon.', 'surecart')}</div>
                </div>
              </sc-stacked-list-row>
            </sc-stacked-list>
          </sc-card>
        </sc-dashboard-module>
      </sc-spacing>
    );
  }

  render() {
    if (this.loading || !this.fulfillments?.length) {
      // awaitingFulfillment only flips after both fetches resolve, so nothing flashes mid-flight.
      if (this.awaitingFulfillment && this.showPreShipmentNotice) return this.renderOrderReceived();
      return <Host style={{ display: 'none' }}></Host>;
    }

    return (
      <sc-spacing style={{ '--spacing': 'var(--sc-spacing-large)' }}>
        <sc-dashboard-module error={this.error}>
          <span slot="heading">{this.heading || _n('Shipment', 'Shipments', this.fulfillments?.length, 'surecart')}</span>
          {this.fulfillments.map(fulfillment => {
            // A purchased label wins over a voided one it replaced — data[0] may still be the voided original.
            const shipments = fulfillment?.shipments?.data || [];
            const shipment = shipments.find(s => s?.status === 'purchased') || shipments.find(s => s?.status !== 'voided') || shipments[0];
            // Shipment tracking wins; otherwise merchant-entered trackings still show even when a draft/voided shipment record exists.
            const hasShipmentTracking = shipment?.status === 'purchased' && !!shipment?.tracking_number;
            const trackings = hasShipmentTracking ? [{ number: shipment.tracking_number, url: shipment.tracking_url }] : fulfillment?.trackings?.data || [];
            return (
              <sc-card noPadding>
                <sc-stacked-list>
                  <sc-stacked-list-row>
                    <div class="fulfillment__header">
                      {/* A voided shipment supersedes the stale shipment_status (matches admin). */}
                      <sc-fulfillment-shipping-status-badge status={shipment?.status === 'voided' ? 'voided' : fulfillment.shipment_status} context="customer" />
                      <div class="fulfillment__number">#{fulfillment?.number}</div>
                    </div>
                  </sc-stacked-list-row>

                  {!!trackings.length && (
                    <sc-stacked-list-row>
                      <div class="trackings">
                        <sc-icon name="truck" />
                        <div class="trackings__details">
                          <div class="trackings__title">
                            {hasShipmentTracking && shipment?.carrier
                              ? sprintf(__('Tracking number via %s', 'surecart'), shipment.carrier)
                              : _n('Tracking number', 'Tracking numbers', trackings.length, 'surecart')}
                          </div>
                          <div class="trackings__list">
                            {trackings.map(({ number, url }) => (
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                {number}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </sc-stacked-list-row>
                  )}

                  {(fulfillment?.fulfillment_items?.data || []).map(({ id, line_item, quantity }) => {
                    return (
                      <sc-stacked-list-row key={id} style={{ '--columns': '2' }}>
                        <div>
                          <div class="line_item__info">
                            <div class="line_item__image">
                              {!!(line_item?.price?.product as Product)?.line_item_image && <img {...((line_item?.price?.product as Product)?.line_item_image as any)} />}
                            </div>
                            <div class="line_item__text">
                              <div>{(line_item?.price?.product as Product)?.name}</div>
                              <div>
                                {!!(line_item?.price?.product as Product)?.weight && (
                                  <sc-format-number type="unit" value={(line_item?.price?.product as Product)?.weight} unit={(line_item?.price?.product as Product)?.weight_unit} />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <span>{sprintf(__('Qty: %d', 'surecart'), quantity || 0)}</span>
                      </sc-stacked-list-row>
                    );
                  })}
                </sc-stacked-list>
              </sc-card>
            );
          })}
        </sc-dashboard-module>
      </sc-spacing>
    );
  }
}
